import { useCallback, useEffect, type ReactNode } from 'react';
import { Box, Button, EmptyState } from '@citibox-panel/ui';
import { useSearchTabs } from '../hooks/useSearchTabs';
import type { LocationTab, SearchTab } from '../model/shellState';
import { ErrorNoticeProvider } from './ErrorNoticeProvider';
import { SearchShellHeader } from './SearchShellHeader';
import { EntitySubTabs } from './EntitySubTabs';
import { TabContent } from './TabContent';

export interface SearchShellProps {
  // apps/shell injects its <UserMenu/> here; the shell module stays generic.
  headerEnd?: ReactNode;
  // H2 injects the real phone search input; H1 leaves the slot empty.
  searchSlot?: ReactNode;
  // Fired by the "+" button, the empty-state action and ⌘K. H2 wires it to open the search input.
  onNewSearch?: () => void;
  // The host (apps/shell) injects the «Usuario» ficha here; the shell stays generic.
  renderUser?: (search: SearchTab) => ReactNode;
  // The host injects the «Entrega» detail here; the delivery id is the active sub-tab.
  renderDelivery?: (search: SearchTab, deliveryId: string) => ReactNode;
  // The host injects the building's deliveries list here. Narrowed to a `LocationTab`, so the
  // view reads the address and the filters without re-deriving the state model's guarantee.
  renderLocation?: (search: LocationTab) => ReactNode;
  // Fill the viewport (minHeight 100vh) so the app home is full-height. Set false
  // to size to content instead — e.g. embedding the shell in a showcase/preview
  // that shouldn't reserve a whole screen. Defaults to true.
  fillViewport?: boolean;
  // Per-deployment market label ("Soporte ES") shown in the header brand pill; the host
  // resolves it from runtime config and injects it so the slice stays env-agnostic.
  brandLabel?: string | undefined;
  // When true the brand pill takes the warning treatment (fail-loud on a broken config).
  brandDegraded?: boolean | undefined;
}

export function SearchShell({
  headerEnd,
  searchSlot,
  onNewSearch,
  renderUser,
  renderDelivery,
  renderLocation,
  fillViewport = true,
  brandLabel,
  brandDegraded,
}: SearchShellProps) {
  const tabs = useSearchTabs();

  const handleNewSearch = useCallback(() => {
    onNewSearch?.();
  }, [onNewSearch]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        handleNewSearch();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [handleNewSearch]);

  const activeSearch = tabs.searches.find((s) => s.key === tabs.activeKey) ?? null;

  return (
    // The whole shell inside the notice host, header included: the search slot lives in the header
    // and is one of the callers (ADR 0021), so the provider has to sit above both halves.
    <ErrorNoticeProvider>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          ...(fillViewport ? { minHeight: '100vh' } : {}),
        }}
      >
        <SearchShellHeader
          searches={tabs.searches}
          activeKey={tabs.activeKey}
          onSelect={tabs.setActiveSearch}
          onClose={tabs.closeSearch}
          onNewSearch={handleNewSearch}
          searchSlot={searchSlot}
          headerEnd={headerEnd}
          brandLabel={brandLabel}
          brandDegraded={brandDegraded}
        />
        {activeSearch ? (
          <>
            <EntitySubTabs
              search={activeSearch}
              onSelectSubtab={(id) => {
                tabs.setActiveSubtab(activeSearch.key, id);
              }}
              onCloseDelivery={(id) => {
                tabs.closeDelivery(activeSearch.key, id);
              }}
            />
            <TabContent
              key={activeSearch.key}
              search={activeSearch}
              {...(renderUser ? { renderUser } : {})}
              {...(renderDelivery ? { renderDelivery } : {})}
              {...(renderLocation ? { renderLocation } : {})}
            />
          </>
        ) : (
          <EmptyState
            title="Sin búsquedas abiertas"
            description="Empieza una búsqueda para ver la información del caso."
            action={<Button onClick={handleNewSearch}>Nueva búsqueda</Button>}
          />
        )}
      </Box>
    </ErrorNoticeProvider>
  );
}
